import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useAuth } from 'context/AuthContext';
import { Button } from 'components/common/Button';
import { AuthHero } from 'components/auth/AuthHero';
import { OrganizationService } from 'services/org/organization.service';
import { storageKeys } from 'config/app.config';

const steps = ['Your details', 'Your store'];

interface RegistrationForm {
  name: string; email: string; password: string; confirmPassword: string;
  organization: string; store: string;
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<RegistrationForm>({
    name: '', email: '', password: '', confirmPassword: '', organization: '', store: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationSent, setConfirmationSent] = useState(false);

  const update = (key: keyof RegistrationForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(current => ({ ...current, [key]: event.target.value }));
  };

  const next = () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) { setError('Complete all account fields.'); return; }
    if (form.password.length < 8) { setError('Use a password with at least 8 characters.'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    setError('');
    setStep(current => current + 1);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (step === 0) { next(); return; }
    if (!form.store.trim()) { setError('Enter your store name.'); return; }
    setLoading(true);
    setError('');
    const onboarding = { name: (form.organization.trim() || form.store.trim()), store_name: form.store.trim() };
    try {
      localStorage.setItem(storageKeys.pendingOnboarding, JSON.stringify(onboarding));
      const requiresConfirmation = await register(form.name.trim(), form.email.trim(), form.password);
      if (requiresConfirmation) {
        setConfirmationSent(true);
        return;
      }
      try {
        await OrganizationService.bootstrap({ ...onboarding, full_name: form.name.trim() });
        localStorage.removeItem(storageKeys.pendingOnboarding);
        navigate('/dashboard');
      } catch (bootstrapError: any) {
        setError(bootstrapError?.message || 'Your account was created, but organization setup needs to be completed.');
        navigate('/onboarding');
      }
    } catch (registrationError: any) {
      setError(registrationError?.message || 'Unable to complete registration.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-start bg-[#f7f8f5] px-4 py-4 lg:items-center lg:justify-center lg:px-0 lg:py-0">
      <div className="grid w-full items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(560px,1.1fr)] lg:items-center lg:gap-0">
        <AuthHero signUp />
        <div className="w-full max-w-lg justify-self-center lg:mr-8">
        <div className="mb-6 text-center"><h1 className="text-3xl font-bold tracking-tight text-[#17231c]">Start your free trial</h1></div>
        <div className="flex items-center gap-2 mb-5">
          {steps.map((label, index) => <React.Fragment key={label}><div className={`flex items-center gap-1.5 text-xs ${index <= step ? 'text-blue-600 font-medium' : 'text-gray-400'}`}><span className={`h-6 w-6 rounded-full flex items-center justify-center font-bold ${index < step ? 'bg-green-500 text-white' : index === step ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>{index < step ? <CheckCircle size={14} /> : index + 1}</span>{label}</div>{index < steps.length - 1 && <div className="flex-1 h-px bg-gray-200" />}</React.Fragment>)}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}
          {confirmationSent ? <div className="text-center space-y-3"><CheckCircle size={48} className="mx-auto text-green-500" /><h2 className="text-base font-semibold text-gray-900">Confirm your email</h2><p className="text-sm text-gray-500">We sent a confirmation link to <strong>{form.email}</strong>. Click it, then sign in — your store will be set up automatically.</p><Link to="/login" className="inline-block text-sm text-blue-600 hover:underline">Go to sign in</Link></div> : <form onSubmit={submit} className="space-y-4">
            {step === 0 && <><h2 className="text-base font-semibold text-gray-900">Create your account</h2>{[['name','Full name','text'],['email','Email','email'],['password','Password','password'],['confirmPassword','Confirm password','password']].map(([key, label, type]) => <div key={key}><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label><input required type={type} value={form[key as keyof RegistrationForm] as string} onChange={update(key as keyof RegistrationForm)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div>)}<Button type="submit" className="w-full justify-center">Continue</Button></>}
            {step === 1 && <><h2 className="text-base font-semibold text-gray-900">Tell us about your store</h2><div><label className="block text-sm font-medium text-gray-700 mb-1">Store name</label><input required autoFocus value={form.store} onChange={update('store')} placeholder="Main Street Fuel & Go" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Business name <span className="font-normal text-gray-400">(optional)</span></label><input value={form.organization} onChange={update('organization')} placeholder="If you run more than one store" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" /></div><div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800"><p className="font-medium">14-day free trial — no credit card needed</p><p className="mt-1 text-xs">After the trial, Pellvr is $49 per store per month ($39 billed annually). Cancel any time.</p></div><div className="flex gap-2"><Button type="button" variant="secondary" onClick={() => setStep(0)}>Back</Button><Button loading={loading} type="submit" className="flex-1 justify-center">Start free trial</Button></div><p className="text-xs text-gray-500">By starting a trial you agree to the <a className="underline" href="https://pellvr.com/terms.html" target="_blank" rel="noreferrer">Terms of Service</a> and <a className="underline" href="https://pellvr.com/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>.</p></>}
          </form>}
          <p className="mt-5 text-center text-xs text-gray-500">Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link></p>
        </div>
      </div>
      </div>
    </div>
  );
}
